import { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { gridUnit } from 'src/design/styleguide/common/measurements';
import { breakpointSmall } from 'src/design/styleguide/common/breakpoints';
import Spacing from 'src/design/styleguide/spacing/Spacing';
import Text from 'src/design/styleguide/text/Text';
import Paragraph from 'src/design/components/bodyText/Paragraph';
import { Heading } from 'src/design/styleguide/Headings';
import PrimaryButton from 'src/design/components/button/PrimaryButton';
import SecondaryButton from 'src/design/components/button/SecondaryButton';
import Loading from 'src/components/loading/Loading';
import UserService from 'scripts/services/UserService';
import { errorAlert, successAlert } from 'scripts/redux/actions/alerts';
import { showModal as showModalAction } from 'src/components/modals/modalActions';
import Formatters from 'scripts/utilities/formattersV2';
import { layoutStyles } from 'src/components/dashboard/common/styles';

const ItemCard = styled.div`
  position: relative;
  overflow: hidden;
  padding: ${gridUnit * 3}px;
  border: 1px solid var(--bx-color---grey-200);
  border-radius: var(--bx-radius);
  background: var(--bx-color---surface);

  &.active::before,
  &.pending::before,
  &.blocked::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 3px;
  }

  &.active::before {
    background: var(--bx-color---primary);
  }

  &.pending::before {
    background: var(--bx-color---yolk);
  }

  &.blocked::before {
    background: var(--bx-color---grey-500);
  }
`;

const CardsContainer = styled.div`
  ${layoutStyles}
  grid-template-columns: minmax(0, 1fr);
  margin-top: ${gridUnit * 4}px;

  ${breakpointSmall(`margin-top: ${gridUnit * 6}px;`)}
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 30%) minmax(0, 70%);
  grid-column-gap: ${gridUnit * 5}px;
  grid-row-gap: ${gridUnit}px;
`;

const DetailName = styled(Text).attrs({
  fontSize: '4x-small',
})`
  color: var(--bx-color---text-subdued);
`;

const DetailValue = styled(Text).attrs({
  fontSize: '4x-small',
  fontWeight: 'medium',
})`
  color: var(--bx-color---on-surface-contrast-800);
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: ${gridUnit * 2}px;
  margin-top: ${gridUnit * 2}px;
  padding-top: ${gridUnit * 2}px;
  border-top: 1px solid var(--bx-color---grey-100);

  > * {
    min-width: ${gridUnit * 11}px;
    text-align: center;
  }

  ${breakpointSmall(`
    justify-content: flex-end;
  `)}
`;

const ConfirmationModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ConfirmationButtonsContainer = styled.div`
  display: flex;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${gridUnit * 2}px;
  padding-bottom: ${gridUnit * 2}px;
  border-bottom: 1px solid var(--bx-color---grey-100);
`;

const StatusBadge = styled(Text).attrs({
  as: 'span',
  fontSize: '5x-small',
  fontWeight: 'bold',
})`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border: 1px solid;
  border-radius: var(--bx-radius);
  letter-spacing: 0.04em;
  text-transform: uppercase;

  &.active {
    color: var(--bx-color---primary);
    background: var(--bx-color---pink-50);
    border-color: var(--bx-color---primary);
  }

  &.pending {
    color: var(--bx-color---yolk);
    background: var(--bx-color---yellow-50);
    border-color: var(--bx-color---yolk);
  }

  &.blocked {
    color: var(--bx-color---grey-700);
    background: var(--bx-color---grey-100);
    border-color: var(--bx-color---grey-500);
  }
`;

const normalizeActiveAdvisor = (payload) => {
  if (!payload) return null;
  if (Object.prototype.hasOwnProperty.call(payload, 'advisor')) {
    return payload.advisor;
  }
  return payload;
};

const normalizePendingRequests = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && Array.isArray(payload.pendingRequests)) {
    return payload.pendingRequests;
  }

  if (payload && Array.isArray(payload.requests)) {
    return payload.requests;
  }

  return [];
};

const normalizeBlockedRelationships = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && Array.isArray(payload.blockedRelationships)) {
    return payload.blockedRelationships;
  }

  return [];
};

const formatDate = (value) => {
  if (!value) return '-';
  return Formatters.time.date(value);
};

const ConnectionActions = ({
  className,
  onAccept,
  onDecline,
  onRevoke,
  onBlock,
  onUnblock,
  advisorId,
  processingAction,
}) => (
  <ActionsRow className={className}>
    {onAccept && (
      <PrimaryButton
        size={PrimaryButton.sizes.EXTRA_SMALL}
        color={PrimaryButton.colors.PRIMARY}
        disabled={!!processingAction}
        onClick={() => onAccept(advisorId)}
      >
        {processingAction === 'accept' ? 'Accepting...' : 'Accept'}
      </PrimaryButton>
    )}
    {onDecline && (
      <SecondaryButton
        size={SecondaryButton.sizes.EXTRA_SMALL}
        color={SecondaryButton.colors.PRIMARY}
        disabled={!!processingAction}
        onClick={() => onDecline(advisorId)}
      >
        {processingAction === 'decline' ? 'Declining...' : 'Decline'}
      </SecondaryButton>
    )}
    {onRevoke && (
      <SecondaryButton
        size={SecondaryButton.sizes.EXTRA_SMALL}
        color={SecondaryButton.colors.PRIMARY}
        disabled={!!processingAction}
        onClick={() => onRevoke(advisorId)}
      >
        {processingAction === 'revoke' ? 'Revoking...' : 'Revoke Access'}
      </SecondaryButton>
    )}
    {onBlock && (
      <SecondaryButton
        size={SecondaryButton.sizes.EXTRA_SMALL}
        color={SecondaryButton.colors.SECONDARY}
        disabled={!!processingAction}
        onClick={() => onBlock(advisorId)}
      >
        {processingAction === 'block' ? 'Blocking...' : 'Block'}
      </SecondaryButton>
    )}
    {onUnblock && (
      <PrimaryButton
        size={PrimaryButton.sizes.EXTRA_SMALL}
        color={PrimaryButton.colors.PRIMARY}
        disabled={!!processingAction}
        onClick={() => onUnblock(advisorId)}
      >
        {processingAction === 'unblock' ? 'Unblocking...' : 'Unblock'}
      </PrimaryButton>
    )}
  </ActionsRow>
);

ConnectionActions.propTypes = {
  className: PropTypes.string,
  onAccept: PropTypes.func,
  onDecline: PropTypes.func,
  onRevoke: PropTypes.func,
  onBlock: PropTypes.func,
  onUnblock: PropTypes.func,
  advisorId: PropTypes.string.isRequired,
  processingAction: PropTypes.string,
};

class RevokeConnectionConfirmationModal extends Component {
  static propTypes = {
    closeThisModal: PropTypes.func,
    onConfirm: PropTypes.func.isRequired,
  };

  state = {
    submitting: false,
  };

  submitting = false;

  onConfirm = async () => {
    if (this.submitting) return;

    this.submitting = true;
    this.setState({ submitting: true });

    const isSuccessful = await this.props.onConfirm();
    if (isSuccessful) {
      this.props.closeThisModal();
      return;
    }

    this.submitting = false;
    this.setState({ submitting: false });
  };

  render() {
    const { closeThisModal } = this.props;
    const { submitting } = this.state;

    return (
      <ConfirmationModalContainer>
        <Heading as="p">
          Are you sure you
          <br />
          want to revoke this adviser connection?
        </Heading>
        <Spacing top="x-large">
          <Paragraph align="center">
            This will remove the adviser&apos;s access to your account. You can
            reconnect later, but the adviser will need to request access again
            after 24 hours.
          </Paragraph>
        </Spacing>
        <Spacing top="5x-large">
          <ConfirmationButtonsContainer>
            <SecondaryButton
              color={SecondaryButton.colors.PRIMARY}
              disabled={submitting}
              onClick={closeThisModal}
            >
              No
            </SecondaryButton>
            <Spacing left="x-small">
              <PrimaryButton
                color={PrimaryButton.colors.SECONDARY}
                disabled={submitting}
                onClick={this.onConfirm}
              >
                {submitting ? 'Revoking...' : 'Yes, revoke access'}
              </PrimaryButton>
            </Spacing>
          </ConfirmationButtonsContainer>
        </Spacing>
      </ConfirmationModalContainer>
    );
  }
}

class BlockConnectionConfirmationModal extends Component {
  static propTypes = {
    closeThisModal: PropTypes.func,
    onConfirm: PropTypes.func.isRequired,
  };

  state = {
    submitting: false,
  };

  submitting = false;

  onConfirm = async () => {
    if (this.submitting) return;

    this.submitting = true;
    this.setState({ submitting: true });

    const isSuccessful = await this.props.onConfirm();
    if (isSuccessful) {
      this.props.closeThisModal();
      return;
    }

    this.submitting = false;
    this.setState({ submitting: false });
  };

  render() {
    const { closeThisModal } = this.props;
    const { submitting } = this.state;

    return (
      <ConfirmationModalContainer>
        <Heading as="p">
          Are you sure you
          <br />
          want to block this adviser?
        </Heading>
        <Spacing top="x-large">
          <Paragraph align="center">
            This removes any current access or pending request and prevents this
            adviser from requesting access again. You can unblock them later.
          </Paragraph>
        </Spacing>
        <Spacing top="5x-large">
          <ConfirmationButtonsContainer>
            <SecondaryButton
              color={SecondaryButton.colors.PRIMARY}
              disabled={submitting}
              onClick={closeThisModal}
            >
              No
            </SecondaryButton>
            <Spacing left="x-small">
              <PrimaryButton
                color={PrimaryButton.colors.SECONDARY}
                disabled={submitting}
                onClick={this.onConfirm}
              >
                {submitting ? 'Blocking...' : 'Yes, block adviser'}
              </PrimaryButton>
            </Spacing>
          </ConfirmationButtonsContainer>
        </Spacing>
      </ConfirmationModalContainer>
    );
  }
}

@connect(null, {
  errorAlert,
  successAlert,
  showModal: showModalAction,
})
export default class AdvisorConnectionSection extends Component {
  loadRequestId = 0;

  requestInFlight = false;

  unmounted = false;

  static propTypes = {
    errorAlert: PropTypes.func.isRequired,
    successAlert: PropTypes.func.isRequired,
    showModal: PropTypes.func.isRequired,
    user: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  };

  state = {
    loading: true,
    activeAdvisor: null,
    pendingRequests: [],
    blockedRelationships: [],
    processingAdvisorId: null,
    processingAction: null,
  };

  componentDidMount() {
    this.unmounted = false;
    this.loadData();
  }

  componentDidUpdate(previousProps) {
    if (previousProps.user.id !== this.props.user.id) {
      this.loadData();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.loadRequestId += 1;
  }

  loadData = async ({ showLoader = true } = {}) => {
    const { user } = this.props;
    const currentLoadRequestId = ++this.loadRequestId;

    if (!user.id) {
      this.setState({
        loading: false,
        activeAdvisor: null,
        pendingRequests: [],
        blockedRelationships: [],
      });
      return;
    }

    if (showLoader) {
      this.setState({ loading: true });
    }

    try {
      const [
        activeAdvisorPayload,
        pendingRequestsPayload,
        blockedRelationshipsPayload,
      ] = await Promise.all([
        UserService.getActiveAdvisor(user.id).catch((error) => {
          return { __advisorError: error };
        }),
        UserService.getPendingAdvisorRequests(user.id).catch((error) => {
          return { __pendingRequestsError: error };
        }),
        UserService.getBlockedAdvisorRelationships(user.id).catch((error) => {
          return { __blockedRelationshipsError: error };
        }),
      ]);

      const activeAdvisorError =
        activeAdvisorPayload && activeAdvisorPayload.__advisorError;
      const pendingRequestsError =
        pendingRequestsPayload && pendingRequestsPayload.__pendingRequestsError;
      const blockedRelationshipsError =
        blockedRelationshipsPayload &&
        blockedRelationshipsPayload.__blockedRelationshipsError;

      if (this.unmounted || currentLoadRequestId !== this.loadRequestId) {
        return;
      }

      const activeAdvisor = activeAdvisorError
        ? null
        : normalizeActiveAdvisor(activeAdvisorPayload);

      const pendingRequests = pendingRequestsError
        ? []
        : normalizePendingRequests(pendingRequestsPayload);
      const blockedRelationships = blockedRelationshipsError
        ? []
        : normalizeBlockedRelationships(blockedRelationshipsPayload);

      const loadError =
        activeAdvisorError || pendingRequestsError || blockedRelationshipsError;
      if (loadError) {
        this.props.errorAlert(loadError.message);
      }

      this.setState({
        loading: false,
        activeAdvisor,
        pendingRequests,
        blockedRelationships,
      });
    } catch (error) {
      if (this.unmounted || currentLoadRequestId !== this.loadRequestId) {
        return;
      }

      this.props.errorAlert(error.message);
      this.setState({
        loading: false,
        activeAdvisor: null,
        pendingRequests: [],
        blockedRelationships: [],
      });
    }
  };

  processRequest = async ({ advisorId, action, request, successMessage }) => {
    if (this.requestInFlight) return false;

    this.requestInFlight = true;
    const { user } = this.props;

    this.setState({
      processingAdvisorId: advisorId,
      processingAction: action,
    });

    try {
      await request(user.id, advisorId);
      if (this.unmounted) return false;

      this.props.successAlert(successMessage);
      this.setState({
        processingAdvisorId: null,
        processingAction: null,
      });
      await this.loadData({ showLoader: false });
      this.requestInFlight = false;
      return true;
    } catch (error) {
      this.requestInFlight = false;
      if (this.unmounted) return false;

      this.props.errorAlert(error.message);
      this.setState({
        processingAdvisorId: null,
        processingAction: null,
      });
      return false;
    }
  };

  onAccept = (advisorId) =>
    this.processRequest({
      advisorId,
      action: 'accept',
      request: UserService.acceptAdvisorRequest,
      successMessage: 'Adviser connection request accepted.',
    });

  onDecline = (advisorId) =>
    this.processRequest({
      advisorId,
      action: 'decline',
      request: UserService.declineAdvisorRequest,
      successMessage: 'Adviser connection request declined.',
    });

  performRevoke = (advisorId) =>
    this.processRequest({
      advisorId,
      action: 'revoke',
      request: UserService.revokeAdvisorConnection,
      successMessage: 'Adviser access revoked.',
    });

  onRevoke = (advisorId) =>
    this.props.showModal({
      content: (
        <RevokeConnectionConfirmationModal
          onConfirm={() => this.performRevoke(advisorId)}
        />
      ),
      testRef: 'advisor-revoke-confirmation-modal',
    });

  performBlock = (advisorId) =>
    this.processRequest({
      advisorId,
      action: 'block',
      request: UserService.blockAdvisorConnection,
      successMessage: 'Adviser blocked.',
    });

  onBlock = (advisorId) =>
    this.props.showModal({
      content: (
        <BlockConnectionConfirmationModal
          onConfirm={() => this.performBlock(advisorId)}
        />
      ),
      testRef: 'advisor-block-confirmation-modal',
    });

  onUnblock = (advisorId) =>
    this.processRequest({
      advisorId,
      action: 'unblock',
      request: UserService.unblockAdvisorConnection,
      successMessage: 'Adviser unblocked. They can now request access again.',
    });

  renderRequestCard = (relationship, title, actions) => {
    const { processingAdvisorId, processingAction } = this.state;
    const {
      advisorId,
      name,
      email,
      companyName,
      afslNumber,
      requestedAt,
      createdAt,
    } = relationship;

    return (
      <ItemCard className="pending" key={`${title}-${advisorId}`}>
        <CardHeader>
          <StatusBadge className="pending">{title}</StatusBadge>
        </CardHeader>
        <Spacing top="normal">
          <DetailsGrid>
            <DetailName>Adviser Name</DetailName>
            <DetailValue>{name || '-'}</DetailValue>
            <DetailName>Email</DetailName>
            <DetailValue>{email || '-'}</DetailValue>
            <DetailName>Company</DetailName>
            <DetailValue>{companyName || '-'}</DetailValue>
            <DetailName>AFSL Number</DetailName>
            <DetailValue>{afslNumber || '-'}</DetailValue>
            <DetailName>Requested</DetailName>
            <DetailValue>{formatDate(requestedAt || createdAt)}</DetailValue>
          </DetailsGrid>
        </Spacing>
        <Spacing top="normal">
          <ConnectionActions
            advisorId={advisorId}
            processingAction={
              processingAdvisorId === advisorId ? processingAction : null
            }
            {...actions}
          />
        </Spacing>
      </ItemCard>
    );
  };

  renderActiveAdvisorCard = (advisor) => {
    const { processingAdvisorId, processingAction } = this.state;
    const advisorId = advisor.advisorId || advisor.id;

    return (
      <ItemCard className="active" key={`active-advisor-${advisorId}`}>
        <CardHeader>
          <StatusBadge className="active">Active Adviser</StatusBadge>
        </CardHeader>
        <Spacing top="normal">
          <DetailsGrid>
            <DetailName>Name</DetailName>
            <DetailValue>{advisor.name || '-'}</DetailValue>
            <DetailName>Email</DetailName>
            <DetailValue>{advisor.email || '-'}</DetailValue>
            <DetailName>Mobile</DetailName>
            <DetailValue>
              {advisor.mobile || advisor.mobilePhone || '-'}
            </DetailValue>
            <DetailName>Company</DetailName>
            <DetailValue>{advisor.companyName || '-'}</DetailValue>
            <DetailName>AFSL Number</DetailName>
            <DetailValue>{advisor.afslNumber || '-'}</DetailValue>
            <DetailName>Company Address</DetailName>
            <DetailValue>{advisor.address || '-'}</DetailValue>
          </DetailsGrid>
        </Spacing>
        <Spacing top="normal">
          <ConnectionActions
            advisorId={advisorId}
            processingAction={
              processingAdvisorId === advisorId ? processingAction : null
            }
            onRevoke={this.onRevoke}
            onBlock={this.onBlock}
          />
        </Spacing>
      </ItemCard>
    );
  };

  renderBlockedAdvisorCard = (relationship) => {
    const { processingAdvisorId, processingAction } = this.state;
    const { advisorId, name, email, companyName, afslNumber, blockedAt } =
      relationship;

    return (
      <ItemCard className="blocked" key={`blocked-advisor-${advisorId}`}>
        <CardHeader>
          <StatusBadge className="blocked">Blocked Adviser</StatusBadge>
        </CardHeader>
        <Spacing top="normal">
          <DetailsGrid>
            <DetailName>Name</DetailName>
            <DetailValue>{name || '-'}</DetailValue>
            <DetailName>Email</DetailName>
            <DetailValue>{email || '-'}</DetailValue>
            <DetailName>Company</DetailName>
            <DetailValue>{companyName || '-'}</DetailValue>
            <DetailName>AFSL Number</DetailName>
            <DetailValue>{afslNumber || '-'}</DetailValue>
            <DetailName>Blocked</DetailName>
            <DetailValue>{formatDate(blockedAt)}</DetailValue>
          </DetailsGrid>
        </Spacing>
        <Spacing top="normal">
          <ConnectionActions
            advisorId={advisorId}
            processingAction={
              processingAdvisorId === advisorId ? processingAction : null
            }
            onUnblock={this.onUnblock}
          />
        </Spacing>
      </ItemCard>
    );
  };

  render() {
    const { loading, activeAdvisor, pendingRequests, blockedRelationships } =
      this.state;

    if (loading) {
      return <Loading notFullHeight testRef="advisor-connection-loading" />;
    }

    const normalizedPendingRequests = normalizePendingRequests(pendingRequests);
    const normalizedBlockedRelationships =
      normalizeBlockedRelationships(blockedRelationships);
    const hasConnectionData =
      !!activeAdvisor ||
      !!normalizedPendingRequests.length ||
      !!normalizedBlockedRelationships.length;

    if (!hasConnectionData) {
      return (
        <Paragraph>
          You currently have no active advisers or pending adviser requests.
        </Paragraph>
      );
    }

    return (
      <CardsContainer>
        {activeAdvisor && this.renderActiveAdvisorCard(activeAdvisor)}
        {normalizedPendingRequests.length > 0 &&
          normalizedPendingRequests.map((request) =>
            this.renderRequestCard(request, 'Pending Request', {
              onAccept: this.onAccept,
              onDecline: this.onDecline,
              onBlock: this.onBlock,
            }),
          )}
        {normalizedBlockedRelationships.length > 0 &&
          normalizedBlockedRelationships.map(this.renderBlockedAdvisorCard)}
      </CardsContainer>
    );
  }
}
